import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Zap, Sparkles, Wrench, Users, Award, ArrowRight } from "lucide-react";

const values = [
  { icon: ShieldCheck, title: "Verified", desc: "Every technician is Aadhar-verified, skill-tested and uniformed." },
  { icon: Sparkles, title: "Transparent", desc: "Flat starting rates. Spares at MRP. Zero hidden charges, ever." },
  { icon: Zap, title: "Fast", desc: "Same-day service for Lucknow. Urgent slots within 4 hrs." },
  { icon: Wrench, title: "Multi-Service", desc: "RO, AC, Fan & Washing Machine — one trusted team for everything." },
];

const milestones = [
  { n: "1000+", l: "Services completed" },
  { n: "500+", l: "Happy homes & offices" },
  { n: "30+", l: "Verified technicians" },
  { n: "4.8/5", l: "Average rating" },
];

export default function About() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      {/* Hero */}
      <section className="text-center max-w-3xl mx-auto" data-testid="about-hero">
        <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">About Repairro</span>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-slate-900 mt-3">
          Making home appliance services <span className="text-[#0369A1]">reliable</span>, <span className="text-[#0369A1]">transparent</span> & tech-driven
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          Repairro was built to fix one thing: the broken, opaque world of home appliance service. We've replaced
          unclear pricing and unverified technicians with simple online booking, fixed rates and Aadhar-verified pros — across Lucknow.
        </p>
      </section>

      {/* Values */}
      <section className="mt-20" data-testid="values-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <div key={v.title} data-testid={`value-${i}`} className="bg-white rounded-2xl p-7 border border-slate-200 card-shadow">
              <div className="w-12 h-12 rounded-xl bg-[#0C4A6E]/10 text-[#0C4A6E] flex items-center justify-center">
                <v.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 mt-5">{v.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Milestones */}
      <section className="mt-20 bg-gradient-to-r from-[#0369A1] to-[#0369A1] rounded-3xl p-10 text-white" data-testid="milestones-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {milestones.map((m, i) => (
            <div key={m.l} data-testid={`milestone-${i}`}>
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-[#CCFBF1]">{m.n}</div>
              <div className="text-sm text-slate-300 mt-2">{m.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="mt-20" data-testid="team-section">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="text-xs font-semibold text-[#0369A1] uppercase tracking-widest">Our team</span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-2">Built by service pros, powered by technology</h2>
          <p className="mt-3 text-slate-600">
            A team of certified technicians, dispatchers and engineers — focused on making every home visit feel premium and predictable.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Customer Care", desc: "Phone + WhatsApp support, 7 AM – 9 PM. Indian languages welcome." },
            { icon: Wrench, title: "Field Technicians", desc: "Brand-trained, Aadhar-verified, uniformed and equipped with original tools." },
            { icon: Award, title: "Quality Audit", desc: "Every job is audited via post-service customer feedback. We learn fast." },
          ].map((t, i) => (
            <div key={t.title} data-testid={`team-${i}`} className="bg-white rounded-2xl p-7 border border-slate-200">
              <t.icon className="w-7 h-7 text-[#0369A1]" />
              <h3 className="font-display font-bold text-lg text-slate-900 mt-4">{t.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-20 bg-white rounded-3xl p-10 sm:p-14 text-center border border-slate-200" data-testid="about-cta">
        <h2 className="font-display font-bold text-3xl text-slate-900">Try Repairro once. Book us for life.</h2>
        <p className="mt-3 text-slate-600 max-w-lg mx-auto">Same-day slots open right now in Lucknow.</p>
        <Link
          to="/booking"
          data-testid="about-book-now"
          className="inline-flex items-center gap-2 mt-7 bg-[#0369A1] hover:bg-[#075985] text-white font-semibold px-7 py-4 rounded-xl transition-all"
        >
          Book a Service <ArrowRight className="w-5 h-5" />
        </Link>
      </section>
    </div>
  );
}
